import React, { useState, useEffect } from 'react';
import { usePlacementManager } from '../../../hooks/usePlacementManager';
import './ConfigTabsShared.css';

interface ThirdPartyScriptsTabProps {
    handleForm: any;
}

const ThirdPartyScriptsTab: React.FC<ThirdPartyScriptsTabProps> = ({ handleForm }) => {
    const { addThirdPartyScript, removeThirdPartyScript } = usePlacementManager(handleForm);
    const [collapsedScripts, setCollapsedScripts] = useState<{ [key: number]: boolean }>({});

    const handleAddScript = () => {
        const currentLength = (handleForm.values.thirdPartyScripts || []).length;
        // Set the new script to be open BEFORE adding it
        setCollapsedScripts(prev => ({
            ...prev,
            [currentLength]: false // New script should be open
        }));
        addThirdPartyScript();
    };

    // Initialize all scripts as collapsed when page loads (only on mount or when completely reset)
    useEffect(() => {
        const scripts = handleForm.values.thirdPartyScripts || [];
        if (scripts.length === 0) {
            setCollapsedScripts({});
            return;
        }

        // Only set collapsed state for scripts that don't have state yet
        setCollapsedScripts(prev => {
            const newState = { ...prev };
            scripts.forEach((_: any, index: any) => {
                if (!(index in newState)) {
                    newState[index] = true; // Default to collapsed for existing scripts
                }
            });
            return newState;
        });
    }, [handleForm.values.thirdPartyScripts?.length]);

    const updateScriptField = (index: number, field: string, value: any) => {
        const updatedScripts = [...handleForm.values.thirdPartyScripts];
        updatedScripts[index] = { ...updatedScripts[index], [field]: value };
        handleForm.setFieldValue('thirdPartyScripts', updatedScripts);
    };

    const toggleScriptEnabled = (index: number, event: React.MouseEvent) => {
        event.stopPropagation(); // Prevent header click
        const script = handleForm.values.thirdPartyScripts[index];
        const newEnabledState = !script.enabled;

        updateScriptField(index, 'enabled', newEnabledState);

        // If disabling, automatically collapse the card
        if (!newEnabledState) {
            setCollapsedScripts(prev => ({
                ...prev,
                [index]: true // Collapse when disabled
            }));
        }
    };

    const toggleCollapse = (index: number, event: React.MouseEvent) => {
        event.stopPropagation();
        setCollapsedScripts(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const handleHeaderClick = (index: number) => {
        setCollapsedScripts(prev => ({
            ...prev,
            [index]: !prev[index]
        }));
    };

    const hasScripts = (handleForm.values.thirdPartyScripts || []).length > 0;

    return (
        <div className="content-section">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h5 className="mb-1">Scripts de Terceiros</h5>
                    <p className="text-muted small mb-0">
                        Configure scripts externos para serem executados em sua tag
                    </p>
                </div>
                {hasScripts && (
                    <button
                        type="button"
                        className="btn-add-config"
                        onClick={handleAddScript}
                    >
                        ➕ Adicionar Script
                    </button>
                )}
            </div>

            {(handleForm.values.thirdPartyScripts || []).map((script: any, index: number) => {
                const isCollapsed = collapsedScripts[index];
                return (
                    <div key={index} className={`config-card ${!script.enabled ? 'disabled' : ''} ${isCollapsed ? 'collapsed' : ''}`}>
                        <div className="config-header" onClick={() => handleHeaderClick(index)}>
                            {/* Left: Collapse button */}
                            <div className="config-header-left">
                                <button
                                    type="button"
                                    className={`collapse-button ${isCollapsed ? 'collapsed' : ''}`}
                                    onClick={(e) => toggleCollapse(index, e)}
                                >
                                    ⌄
                                </button>
                                <div className="config-title">
                                    📄 Script #{index + 1}
                                    {script.name && ` - ${script.name}`}
                                </div>
                            </div>

                            {/* Center: Delivery percentage */}
                            <div className="config-header-center">
                                <div className="status-display">
                                    <span>{script.deliveryPercentage || 100}%</span>
                                    <div className="status-bar">
                                        <div
                                            className="status-fill"
                                            style={{ width: `${script.deliveryPercentage || 100}%` }}
                                        />
                                    </div>
                                    <span className="text-muted small">entrega</span>
                                </div>
                            </div>

                            {/* Right: Script name */}
                            <div className="config-header-right">
                                {/* Switch */}
                                <div className="custom-switch-container">
                                    <div
                                        className={`custom-switch ${script.enabled ? 'enabled' : ''}`}
                                        onClick={(e) => toggleScriptEnabled(index, e)}
                                    >
                                        <div className="custom-switch-handle" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="config-body">
                            {/* Delivery Configuration */}
                            <div className="form-section">
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Porcentagem de Entrega</label>
                                                <div className="percentage-container">
                                                    <span className="percentage-label">Entregar para:</span>
                                                    <input
                                                        type="number"
                                                        min="0"
                                                        max="100"
                                                        value={script.deliveryPercentage || 100}
                                                        className="percentage-input"
                                                        onChange={(e) => {
                                                            const value = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                                                            updateScriptField(index, 'deliveryPercentage', value);
                                                        }}
                                                    />
                                                    <span>% dos usuários</span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Basic Configuration */}
                            <div className="form-section">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Nome do Script</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={script.name || ''}
                                                    placeholder="Ex: Google Analytics"
                                                    onChange={(e) => updateScriptField(index, 'name', e.target.value)}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Tipo</label>
                                                <select
                                                    className="form-select"
                                                    value={script.type || 'script'}
                                                    onChange={(e) => updateScriptField(index, 'type', e.target.value)}
                                                >
                                                    <option value="script">Script</option>
                                                    <option value="overwrite">Overwrite</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="mb-3">
                                            <div className="form-group">
                                                <label className="form-label">Tipo de Input</label>
                                                <select
                                                    className="form-select"
                                                    value={script.inputType || 'url'}
                                                    onChange={(e) => updateScriptField(index, 'inputType', e.target.value)}
                                                >
                                                    <option value="url">URL</option>
                                                    <option value="code">Código</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Overwrite Configuration */}
                            {script.type === 'overwrite' && (
                                <>
                                    <div className="form-section-divider" />
                                    <div className="form-section">
                                        <div className="form-section-title">
                                            🔄 Configurações de Overwrite
                                        </div>
                                        <div className="row">
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <div className="form-group">
                                                        <label className="form-label">Formato *</label>
                                                        <select
                                                            className="form-select"
                                                            value={script.format || ''}
                                                            onChange={(e) => updateScriptField(index, 'format', e.target.value)}
                                                        >
                                                            <option value="">Selecione um formato</option>
                                                            {(() => {
                                                                const placements = handleForm.values.placements || [];
                                                                const gptPlacements = placements.filter((p: any) => p.provider?.toUpperCase() === 'GPT');
                                                                const adpPlacements = placements.filter((p: any) => p.provider?.toUpperCase() === 'ADP');

                                                                return (
                                                                    <>
                                                                        {gptPlacements.length > 0 && (
                                                                            <optgroup label="GPT">
                                                                                {gptPlacements.map((placement: any, idx: number) => (
                                                                                    <option key={`gpt-${idx}`} value={placement.name || placement.displayName}>
                                                                                        {placement.displayName || placement.name}
                                                                                    </option>
                                                                                ))}
                                                                            </optgroup>
                                                                        )}
                                                                        {adpPlacements.length > 0 && (
                                                                            <optgroup label="ADP">
                                                                                {adpPlacements.map((placement: any, idx: number) => (
                                                                                    <option key={`adp-${idx}`} value={placement.name || placement.displayName}>
                                                                                        {placement.displayName || placement.name}
                                                                                    </option>
                                                                                ))}
                                                                            </optgroup>
                                                                        )}
                                                                    </>
                                                                );
                                                            })()}
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <div className="form-group">
                                                        <label className="form-label">Índice do Formato</label>
                                                        <input
                                                            type="number"
                                                            className="form-control"
                                                            min="0"
                                                            value={script.formatIndex || ''}
                                                            placeholder="0"
                                                            onChange={(e) => updateScriptField(index, 'formatIndex', parseInt(e.target.value) || 0)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}

                            {/* Script Content */}
                            <div className="form-section">
                                {script.inputType === 'url' ? (
                                    <div className="mb-3">
                                        <div className="form-group">
                                            <label className="form-label">URL do Script *</label>
                                            <input
                                                type="url"
                                                className="form-control"
                                                value={script.src || ''}
                                                placeholder="https://exemplo.com/script.js"
                                                onChange={(e) => updateScriptField(index, 'src', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="mb-3">
                                        <div className="form-group">
                                            <label className="form-label">Código do Script *</label>
                                            <textarea
                                                className="form-control"
                                                rows={8}
                                                value={script.text || ''}
                                                placeholder="console.log('Script personalizado');"
                                                onChange={(e) => updateScriptField(index, 'text', e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Script Controls */}
                        <div className="config-controls">
                            <div className="d-flex align-items-center gap-2">
                                <span className="text-muted small">
                                    Criado em: {new Date().toLocaleDateString()}
                                </span>
                            </div>
                            <button
                                type="button"
                                className="btn-remove-config"
                                onClick={() => removeThirdPartyScript(index)}
                                title="Remover script"
                            >
                                🗑️ Remover
                            </button>
                        </div>
                    </div>
                );
            })}

            {(handleForm.values.thirdPartyScripts || []).length === 0 && (
                <div className="empty-config-container">
                    <div className="empty-config-icon">📄</div>
                    <h4 className="empty-config-title">Nenhum script configurado</h4>
                    <p className="empty-config-subtitle">
                        Adicione scripts de terceiros para integrar serviços externos como
                        analytics, pixels de conversão, chatbots e outras ferramentas.
                    </p>
                    <button
                        type="button"
                        className="btn-add-config"
                        onClick={handleAddScript}
                    >
                        ➕ Criar Primeiro Script
                    </button>
                </div>
            )}
        </div>
    );
};

export default ThirdPartyScriptsTab;